import React from 'react';
import { motion, useInView } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import inTouchLogo from '../assets/inTouchLogo2025.png';

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const services = [
  {
    title: 'Touch Screen Kiosks',
    description:
      'Custom-built interactive hardware for events, retail, and information centers.',
    icon: FiIcons.FiMonitor,
    color: 'from-blue-500 to-cyan-400',
    glowColor: 'rgba(59,130,246,0.35)',
  },
  {
    title: 'Event Software',
    description:
      'End-to-end registration, check-in, and attendee management solutions.',
    icon: FiIcons.FiCommand,
    color: 'from-purple-500 to-pink-400',
    glowColor: 'rgba(168,85,247,0.35)',
  },
  {
    title: 'Digital Signage',
    description:
      'Dynamic content delivery systems that engage and inform your audience.',
    icon: FiIcons.FiCast,
    color: 'from-emerald-500 to-teal-400',
    glowColor: 'rgba(16,185,129,0.35)',
  },
  {
    title: 'Gamification',
    description:
      'Interactive games and leaderboards to boost engagement and retention.',
    icon: FiIcons.FiPlayCircle,
    color: 'from-orange-500 to-amber-400',
    glowColor: 'rgba(249,115,22,0.35)',
  },
  {
    title: 'Custom Development',
    description:
      'Tailored software solutions designed to meet your specific interactive needs.',
    icon: FiIcons.FiCode,
    color: 'from-indigo-500 to-violet-400',
    glowColor: 'rgba(99,102,241,0.35)',
  },
  {
    title: 'Analytics & Data',
    description:
      'Real-time insights into user interaction and engagement metrics.',
    icon: FiIcons.FiBarChart2,
    color: 'from-rose-500 to-red-400',
    glowColor: 'rgba(244,63,94,0.35)',
  },
];

const stats = [
  { label: 'Events Powered', value: '500+', icon: FiIcons.FiZap },
  { label: 'Years Experience', value: '12+', icon: FiIcons.FiAward },
  { label: 'Cities Reached', value: '50+', icon: FiIcons.FiMapPin },
  { label: 'Uptime', value: '99%', icon: FiIcons.FiShield },
];

/* ------------------------------------------------------------------ */
/*  Framer Motion variants                                             */
/* ------------------------------------------------------------------ */

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
});

const fadeScale = (delay = 0) => ({
  initial: { opacity: 0, scale: 0.88 },
  animate: { opacity: 1, scale: 1 },
  transition: { delay, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
});

/* ------------------------------------------------------------------ */
/*  SVG Wave Divider                                                   */
/* ------------------------------------------------------------------ */

const WaveDivider = () => (
  <div className="relative w-full h-16 sm:h-20 my-4 sm:my-6 overflow-hidden">
    <svg
      className="absolute inset-0 w-full h-full"
      viewBox="0 0 1440 80"
      preserveAspectRatio="none"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="wave-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#0056b3" stopOpacity="0" />
          <stop offset="30%" stopColor="#0056b3" stopOpacity="0.15" />
          <stop offset="50%" stopColor="#0ea5e9" stopOpacity="0.25" />
          <stop offset="70%" stopColor="#0056b3" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#0056b3" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M0 40 C 240 10, 480 70, 720 40 C 960 10, 1200 70, 1440 40"
        stroke="url(#wave-grad)"
        strokeWidth="2"
        fill="none"
      />
      <path
        d="M0 50 C 360 20, 540 80, 720 50 C 900 20, 1080 80, 1440 50"
        stroke="url(#wave-grad)"
        strokeWidth="1.5"
        fill="none"
        opacity="0.5"
      />
    </svg>
    {/* Centre accent dot */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-accent shadow-[0_0_12px_rgba(250,204,21,0.6)]" />
  </div>
);

/* ------------------------------------------------------------------ */
/*  CTA Circuit Pattern (decorative SVG)                               */
/* ------------------------------------------------------------------ */

const CircuitPattern = () => (
  <svg
    className="absolute inset-0 w-full h-full opacity-[0.08]"
    viewBox="0 0 800 400"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Hexagons */}
    <path d="M100 80 L140 60 L180 80 L180 120 L140 140 L100 120 Z" stroke="white" strokeWidth="1.5" />
    <path d="M600 50 L640 30 L680 50 L680 90 L640 110 L600 90 Z" stroke="white" strokeWidth="1.5" />
    <path d="M350 300 L390 280 L430 300 L430 340 L390 360 L350 340 Z" stroke="white" strokeWidth="1.5" />
    <path d="M700 280 L740 260 L780 280 L780 320 L740 340 L700 320 Z" stroke="white" strokeWidth="1" />
    <path d="M20 250 L60 230 L100 250 L100 290 L60 310 L20 290 Z" stroke="white" strokeWidth="1" />
    {/* Circuit lines */}
    <line x1="180" y1="100" x2="300" y2="100" stroke="white" strokeWidth="1" strokeDasharray="6 4" />
    <line x1="300" y1="100" x2="300" y2="200" stroke="white" strokeWidth="1" strokeDasharray="6 4" />
    <line x1="300" y1="200" x2="500" y2="200" stroke="white" strokeWidth="1" strokeDasharray="6 4" />
    <line x1="500" y1="200" x2="600" y2="70" stroke="white" strokeWidth="1" strokeDasharray="6 4" />
    <line x1="430" y1="320" x2="700" y2="300" stroke="white" strokeWidth="1" strokeDasharray="6 4" />
    <line x1="100" y1="270" x2="350" y2="320" stroke="white" strokeWidth="1" strokeDasharray="6 4" />
    {/* Circuit nodes */}
    <circle cx="300" cy="100" r="3" fill="white" />
    <circle cx="300" cy="200" r="3" fill="white" />
    <circle cx="500" cy="200" r="3" fill="white" />
    <circle cx="140" cy="100" r="2.5" fill="white" />
    <circle cx="640" cy="70" r="2.5" fill="white" />
    <circle cx="390" cy="320" r="2.5" fill="white" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Animated Stat Counter Component                                    */
/* ------------------------------------------------------------------ */

const StatItem = ({ stat, index }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20, scale: 0.92 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ delay: index * 0.12, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="glass-card rounded-2xl p-5 sm:p-6 text-center group hover:shadow-lg transition-shadow duration-300"
    >
      <div className="flex justify-center mb-3">
        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center border border-primary/10 group-hover:border-primary/25 transition-colors duration-300">
          <SafeIcon icon={stat.icon} className="text-primary text-lg sm:text-xl" />
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ delay: index * 0.12 + 0.2, duration: 0.5, type: 'spring', stiffness: 120 }}
        className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-accent mb-1.5 drop-shadow-[0_1px_2px_rgba(250,204,21,0.3)]"
      >
        {stat.value}
      </motion.div>
      <div className="text-slate-600 font-body font-medium text-sm sm:text-base">
        {stat.label}
      </div>
    </motion.div>
  );
};

/* ------------------------------------------------------------------ */
/*  Service Card Component                                             */
/* ------------------------------------------------------------------ */

const ServiceCard = ({ service, index }) => (
  <motion.div
    variants={cardVariants}
    whileHover={{ y: -6, transition: { duration: 0.25, ease: 'easeOut' } }}
    className="glass-card p-6 sm:p-8 rounded-2xl group relative overflow-hidden"
    style={{
      '--card-glow': service.glowColor,
    }}
  >
    {/* Hover glow border overlay */}
    <div
      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
      style={{
        boxShadow: `inset 0 0 0 1.5px ${service.glowColor}, 0 0 24px -4px ${service.glowColor}`,
      }}
    />

    {/* Decorative gradient corner -- larger and richer */}
    <div
      className={`absolute top-0 right-0 w-32 sm:w-40 h-32 sm:h-40 bg-gradient-to-br ${service.color} opacity-[0.06] rounded-bl-full -mr-10 -mt-10 transition-all duration-500 group-hover:opacity-[0.14] group-hover:scale-110`}
    />

    {/* Hover shimmer streak */}
    <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
      <div className="absolute -inset-full bg-gradient-to-r from-transparent via-white/[0.07] to-transparent rotate-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out" />
    </div>

    {/* Icon -- larger, more prominent */}
    <div className="relative mb-6 sm:mb-7">
      <div
        className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white shadow-lg transition-shadow duration-300`}
        style={{
          boxShadow: `0 4px 14px -2px ${service.glowColor}`,
        }}
      >
        <SafeIcon icon={service.icon} className="text-2xl sm:text-[1.75rem]" />
      </div>
      {/* Subtle floating particle on hover */}
      <div
        className={`absolute -top-1 -right-1 w-3 h-3 rounded-full bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-60 transition-all duration-500 group-hover:-translate-y-1 group-hover:translate-x-1`}
      />
    </div>

    <h3 className="text-lg sm:text-xl font-display font-bold text-slate-900 mb-2.5 sm:mb-3 group-hover:text-primary transition-colors duration-300">
      {service.title}
    </h3>

    <p className="text-slate-600 font-body text-sm leading-relaxed">
      {service.description}
    </p>
  </motion.div>
);

/* ------------------------------------------------------------------ */
/*  InTouch Page                                                       */
/* ------------------------------------------------------------------ */

const InTouch = () => {
  return (
    <div className="min-h-screen pt-24 sm:pt-28 pb-16 sm:pb-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">

        {/* ============================================================ */}
        {/*  HERO SECTION                                                 */}
        {/* ============================================================ */}
        <div className="relative text-center max-w-3xl mx-auto mb-10 sm:mb-14">

          {/* Floating orbs / blobs behind the header */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
            <div className="absolute top-0 left-1/4 -translate-x-1/2 w-56 sm:w-72 h-56 sm:h-72 bg-primary/[0.08] rounded-full blur-3xl animate-float" />
            <div className="absolute top-8 right-1/4 translate-x-1/2 w-48 sm:w-64 h-48 sm:h-64 bg-secondary/[0.10] rounded-full blur-3xl animate-float-delayed" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-accent/[0.06] rounded-full blur-3xl animate-pulse-glow" />
          </div>

          {/* Logo container with glow ring */}
          <motion.div
            {...fadeScale(0)}
            className="inline-block relative mb-7 sm:mb-9"
          >
            {/* Outer glow ring */}
            <div className="absolute -inset-3 sm:-inset-4 rounded-3xl bg-gradient-to-br from-primary/20 via-secondary/10 to-accent/10 blur-xl opacity-70 animate-pulse-glow" />
            {/* Ring border */}
            <div className="absolute -inset-1.5 sm:-inset-2 rounded-2xl border-2 border-primary/15 bg-gradient-to-br from-primary/[0.03] to-secondary/[0.03]" />
            {/* Logo card */}
            <div className="relative bg-white rounded-2xl px-5 sm:px-7 py-4 sm:py-5 shadow-xl shadow-primary/[0.08] border border-slate-100">
              <a href="https://intouch.com.na" target="_blank" rel="noopener noreferrer">
                <img
                  src={inTouchLogo}
                  alt="inTouch Interactive Technology"
                  className="h-14 sm:h-20 md:h-24 w-auto object-contain"
                />
              </a>
            </div>
          </motion.div>

          {/* Badge */}
          <motion.div
            {...fadeScale(0.15)}
            className="flex justify-center"
          >
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-1.5 mb-5 sm:mb-6 border border-primary/20">
              <SafeIcon icon={FiIcons.FiCpu} className="text-primary text-sm" />
              <span className="text-sm font-display font-semibold text-primary tracking-wide">
                Technology Partners
              </span>
            </div>
          </motion.div>

          {/* Heading */}
          <motion.h1
            {...fadeUp(0.25)}
            className="text-hero-sub font-display font-bold text-slate-900 mb-4 sm:mb-6 leading-[1.15]"
          >
            We Build{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_100%]">
              Interactive Experiences
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            {...fadeUp(0.35)}
            className="text-base sm:text-lg font-body text-slate-600 leading-relaxed max-w-2xl mx-auto"
          >
            inTouch Interactive Technologies specializes in bridging the gap
            between digital and physical worlds. From hardware rentals to custom
            software, we power your events and business spaces.
          </motion.p>
        </div>

        {/* ============================================================ */}
        {/*  STATS / TRUST BAR                                            */}
        {/* ============================================================ */}
        <div className="mb-6 sm:mb-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {stats.map((stat, index) => (
              <StatItem key={index} stat={stat} index={index} />
            ))}
          </div>
        </div>

        {/* ============================================================ */}
        {/*  WAVE DIVIDER                                                 */}
        {/* ============================================================ */}
        <WaveDivider />

        {/* ============================================================ */}
        {/*  SERVICES SECTION HEADING                                     */}
        {/* ============================================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="text-center mb-10 sm:mb-14"
        >
          <h2 className="text-section-title font-display font-bold text-slate-900 mb-3">
            Our Services
          </h2>
          <p className="text-slate-500 font-body text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            End-to-end interactive solutions designed to captivate audiences and deliver measurable results.
          </p>
        </motion.div>

        {/* ============================================================ */}
        {/*  SERVICES GRID                                                */}
        {/* ============================================================ */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8"
        >
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </motion.div>

        {/* ============================================================ */}
        {/*  CTA SECTION                                                  */}
        {/* ============================================================ */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="mt-16 sm:mt-24 bg-gradient-to-br from-[#003d80] via-[#0056b3] to-[#0284c7] rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-14 text-center relative overflow-hidden"
        >
          {/* Circuit pattern background */}
          <CircuitPattern />

          {/* Soft radial blurs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-28 -left-28 w-56 sm:w-72 h-56 sm:h-72 bg-white/[0.08] rounded-full blur-3xl" />
            <div className="absolute -bottom-28 -right-28 w-56 sm:w-72 h-56 sm:h-72 bg-white/[0.08] rounded-full blur-3xl" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-accent/[0.06] rounded-full blur-2xl animate-pulse-glow" />
          </div>

          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-white mb-3 sm:mb-4">
                Ready to elevate your event?
              </h2>
              <p className="text-white/80 font-body text-sm sm:text-base mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed">
                Get in touch with our team to discuss your requirements and discover
                how we can bring your vision to life with cutting-edge technology.
              </p>
            </motion.div>

            {/* Button with animated pulse ring */}
            <div className="relative inline-block mb-8 sm:mb-10">
              {/* Pulse ring */}
              <div className="absolute inset-0 rounded-full bg-white/20 animate-ping" style={{ animationDuration: '2.5s' }} />
              <div className="absolute -inset-1.5 rounded-full bg-white/10 animate-pulse-glow" />
              <a
                href="https://derick.onwhats.app"
                target="_blank"
                rel="noopener noreferrer"
                className="relative inline-block bg-white text-primary px-8 sm:px-10 py-3.5 sm:py-4 rounded-full font-display font-bold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl hover:shadow-white/20 active:scale-95 text-sm sm:text-base min-h-[48px]"
              >
                Contact Us Today
              </a>
            </div>

            {/* Secondary contact methods */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
              <a
                href="mailto:info@intouch.com.na"
                className="inline-flex items-center gap-2 text-white/75 hover:text-white transition-colors duration-300 text-sm font-body"
              >
                <SafeIcon icon={FiIcons.FiMail} className="text-base" />
                <span>info@intouch.com.na</span>
              </a>
              <a
                href="tel:+264613095910"
                className="inline-flex items-center gap-2 text-white/75 hover:text-white transition-colors duration-300 text-sm font-body"
              >
                <SafeIcon icon={FiIcons.FiPhone} className="text-base" />
                <span>(+264) 61 309 591</span>
              </a>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default InTouch;
